import {
  Component, Input, Output, EventEmitter, OnInit, ChangeDetectionStrategy,
  Optional
} from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DialogRef } from 'app/shared/dialog';
import { NgClass } from '@angular/common';
import { ExtendedModule } from '@angular/flex-layout/extended';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatFormField, MatLabel, MatError, MatSuffix } from '@angular/material/form-field';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatInput } from '@angular/material/input';
import { NgxIntlTelInputModule } from 'ngx-intl-tel-input';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule, provideNativeDateAdapter } from '@angular/material/core';
import { MatTimepickerModule } from '@angular/material/timepicker';

type Weekday = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';
type RuleType = 'include' | 'exclude';

interface TimeBlock { start: string; end: string; }
interface WeeklyRoster {
  effectiveFrom?: string;           // YYYY-MM-DD
  repeatTillDate?: string | null;   // YYYY-MM-DD | null
  days: Record<Weekday, TimeBlock[]>;
}
interface DayOfWeekRule {
  type: RuleType;
  weekday: Weekday;
  weeks: number[];                  // e.g. [2,4] for 2nd & 4th
  fullDay?: boolean;                // if true, ignore blocks
  blocks?: TimeBlock[];
  effectiveFrom?: string;
  repeatTillDate?: string | null;
}
interface DayOfMonthRule {
  type: RuleType;
  dates: number[];                  // 1..31
  fullDay?: boolean;
  blocks?: TimeBlock[];
  effectiveFrom?: string;
  repeatTillDate?: string | null;
}

export interface RosterConfig {
  tz?: string;
  weeklyRoster: WeeklyRoster;
  dayOfWeekRules?: DayOfWeekRule[];
  dayOfMonthRules?: DayOfMonthRule[];
  precedence?: { order: string[]; conflict: 'include_wins' | 'exclude_wins' | 'last_rule_wins' };
}

const WD_ORDER: Weekday[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

@Component({
    selector: 'app-smx-roster-editor',
    templateUrl: './smx-roster-editor.component.html',
    styleUrls: ['./smx-roster-editor.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [provideNativeDateAdapter()],
    imports: [NgClass, ExtendedModule, MatSlideToggle, FormsModule, ReactiveFormsModule, MatFormField, MatLabel, MatInput, MatSuffix, NgxIntlTelInputModule, MatError, MatIconButton, MatIcon, MatButton, MatSelect, MatOption, MatDatepickerModule, MatNativeDateModule, MatTimepickerModule]
})
export class SmxRosterEditorComponent implements OnInit {
  @Input() value: RosterConfig | null = null;
  @Output() valueChange = new EventEmitter<RosterConfig>();
  @Output() save = new EventEmitter<RosterConfig>();

  form!: FormGroup;
  showAdvanced = false;
  showNthWeekday = false;
  showDayOfMonth = false;
  showRepeatWindow = false;

  weekdays = WD_ORDER;
  weekLabel: Record<Weekday, string> = {
    mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun'
  };

  constructor(
    private fb: FormBuilder,
    @Optional() public dialog: DialogRef
  ) { }

  ngOnInit(): void {
    const v = this.value ?? this.defaultValue();
    this.form = this.fb.group({
      tz: [v.tz || 'Asia/Kolkata'],
      weeklyRoster: this.fb.group({
        effectiveFrom: [v.weeklyRoster.effectiveFrom || null],
        repeatTillDate: [v.weeklyRoster.repeatTillDate ?? null],
        days: this.fb.group(
          WD_ORDER.reduce((acc, d) => {
            acc[d] = this.fb.array(
              (v.weeklyRoster.days?.[d] ?? []).map(b => this.makeBlockFG(b.start, b.end))
            );
            return acc;
          }, {} as Record<Weekday, FormArray>)
        )
      }),
      dayOfWeekRules: this.fb.array((v.dayOfWeekRules ?? []).map(r => this.makeDowRuleFG(r))),
      dayOfMonthRules: this.fb.array((v.dayOfMonthRules ?? []).map(r => this.makeDomRuleFG(r))),
      precedence: this.fb.group({
        order: [v.precedence?.order ?? ['weeklyRoster', 'dayOfWeekRules', 'dayOfMonthRules']],
        conflict: [v.precedence?.conflict ?? 'include_wins']
      })
    });

    // Progressive defaults
    this.showAdvanced = (v.dayOfWeekRules?.length || v.dayOfMonthRules?.length) ? true : false;
    this.showNthWeekday = (v.dayOfWeekRules?.length ?? 0) > 0;
    this.showDayOfMonth = (v.dayOfMonthRules?.length ?? 0) > 0;
    this.showRepeatWindow = !!(v.weeklyRoster.effectiveFrom || v.weeklyRoster.repeatTillDate);
  }

  // ---------- Form getters ----------
  daysFA(day: Weekday) { return (this.form.get('weeklyRoster.days.' + day) as FormArray); }
  dowRulesFA() { return this.form.get('dayOfWeekRules') as FormArray; }
  domRulesFA() { return this.form.get('dayOfMonthRules') as FormArray; }

  // ---------- Builders ----------
  private makeBlockFG(start = '09:00', end = '18:00') {
    return this.fb.group({
      start: [start, [Validators.required]],
      end: [end, [Validators.required]],
    }, { validators: [this.timeOrderValidator] });
  }

  private makeDowRuleFG(r?: DayOfWeekRule) {
    const hasWindow = !!(r?.effectiveFrom || r?.repeatTillDate);
    return this.fb.group({
      type: [r?.type ?? 'exclude', Validators.required],
      weekday: [r?.weekday ?? 'sat', Validators.required],
      weeks: [r?.weeks ?? [], Validators.required], // multi-select
      fullDay: [r?.fullDay ?? true],
      blocks: this.fb.array((r?.blocks ?? []).map(b => this.makeBlockFG(b.start, b.end))),
      useWindow: [hasWindow],
      effectiveFrom: [r?.effectiveFrom || null],
      repeatTillDate: [r?.repeatTillDate ?? null]
    });
  }

  private makeDomRuleFG(r?: DayOfMonthRule) {
    const hasWindow = !!(r?.effectiveFrom || r?.repeatTillDate);
    return this.fb.group({
      type: [r?.type ?? 'exclude', Validators.required],
      dates: [r?.dates ?? [], Validators.required], // multi-select
      fullDay: [r?.fullDay ?? true],
      blocks: this.fb.array((r?.blocks ?? []).map(b => this.makeBlockFG(b.start, b.end))),
      useWindow: [hasWindow],
      effectiveFrom: [r?.effectiveFrom || null],
      repeatTillDate: [r?.repeatTillDate ?? null]
    });
  }

  onToggleDowWindow(i: number, on: boolean) {
    const g = this.dowRulesFA().at(i);
    g.get('useWindow')?.setValue(on);
    if (!on) {
      g.get('effectiveFrom')?.setValue(null);
      g.get('repeatTillDate')?.setValue(null);
    }
    this.emitChange();
  }

  onToggleDomWindow(i: number, on: boolean) {
    const g = this.domRulesFA().at(i);
    g.get('useWindow')?.setValue(on);
    if (!on) {
      g.get('effectiveFrom')?.setValue(null);
      g.get('repeatTillDate')?.setValue(null);
    }
    this.emitChange();
  }

  // ---------- Validators ----------
  private timeOrderValidator(group: any) {
    const s = group.get('start')?.value as string;
    const e = group.get('end')?.value as string;
    if (!s || !e) return null;
    return (s < e) ? null : { timeOrder: true };
  }

  // ---------- Weekly blocks actions ----------
  addBlock(day: Weekday) {
    this.daysFA(day).push(this.makeBlockFG());
    this.emitChange();
  }
  removeBlock(day: Weekday, idx: number) {
    this.daysFA(day).removeAt(idx);
    this.emitChange();
  }
  toggleDayOff(day: Weekday, on: boolean) {
    const fa = this.daysFA(day);
    if (on && fa.length === 0) fa.push(this.makeBlockFG(day === 'sat' ? '10:00' : '09:00', day === 'sat' ? '14:00' : '18:00'));
    if (!on) {
      while (fa.length) fa.removeAt(0);
    }
    this.emitChange();
  }
  isDayOn(day: Weekday) { return this.daysFA(day).length > 0; }

  // ---------- Nth weekday rules ----------
  addDowRule() {
    this.dowRulesFA().push(this.makeDowRuleFG({ weeks: [], weekday: 'sat', type: 'exclude', fullDay: true }));
    this.emitChange();
  }
  removeDowRule(i: number) {
    this.dowRulesFA().removeAt(i);
    this.emitChange();
  }
  addDowBlock(i: number) {
    const blocks = (this.dowRulesFA().at(i).get('blocks') as FormArray);
    blocks.push(this.makeBlockFG('10:00', '14:00'));
    this.emitChange();
  }
  removeDowBlock(i: number, j: number) {
    (this.dowRulesFA().at(i).get('blocks') as FormArray).removeAt(j);
    this.emitChange();
  }

  // ---------- Day-of-month rules ----------
  addDomRule() {
    this.domRulesFA().push(this.makeDomRuleFG({ dates: [], type: 'exclude', fullDay: true }));
    this.emitChange();
  }
  removeDomRule(i: number) {
    this.domRulesFA().removeAt(i);
    this.emitChange();
  }
  addDomBlock(i: number) {
    const blocks = (this.domRulesFA().at(i).get('blocks') as FormArray);
    blocks.push(this.makeBlockFG('10:00', '14:00'));
    this.emitChange();
  }
  removeDomBlock(i: number, j: number) {
    (this.domRulesFA().at(i).get('blocks') as FormArray).removeAt(j);
    this.emitChange();
  }

  // ---------- Repeat window toggle ----------
  onToggleRepeatWindow(show: boolean) {
    this.showRepeatWindow = show;
    if (!show) {
      this.form.get('weeklyRoster.effectiveFrom')?.setValue(null);
      this.form.get('weeklyRoster.repeatTillDate')?.setValue(null);
    }
    this.emitChange();
  }

  // ---------- Emit ----------
  emitChange() {
    this.valueChange.emit(this.serialize());
  }
  onSaveClick() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.save.emit(this.serialize());
  }

  // ---------- Serialize ----------
  private serialize(): RosterConfig {
    const raw = this.form.getRawValue();
    // Normalize empty blocks/rules
    const days: Record<Weekday, TimeBlock[]> = {} as any;
    for (const d of WD_ORDER) {
      days[d] = (raw.weeklyRoster.days[d] || []).filter((b: any) => !!b?.start && !!b?.end);
    }
    const weeklyRoster: WeeklyRoster = {
      effectiveFrom: raw.weeklyRoster.effectiveFrom || null,
      repeatTillDate: raw.weeklyRoster.repeatTillDate ?? null,
      days
    };
    const mapBlocks = (arr: any[]) => (arr || []).map(b => ({ start: b.start, end: b.end }));

    const dayOfWeekRules: DayOfWeekRule[] = (raw.dayOfWeekRules || []).map((r: any) => ({
      type: r.type,
      weekday: r.weekday,
      weeks: (r.weeks || []).map((n: any) => Number(n)).filter((n: number) => !!n),
      fullDay: !!r.fullDay,
      blocks: r.fullDay ? undefined : mapBlocks(r.blocks || []),
      effectiveFrom: r.effectiveFrom || null,
      repeatTillDate: r.repeatTillDate ?? null
    }));

    const dayOfMonthRules: DayOfMonthRule[] = (raw.dayOfMonthRules || []).map((r: any) => ({
      type: r.type,
      dates: (r.dates || []).map((n: any) => Number(n)).filter((n: number) => !!n && n <= 31),
      fullDay: !!r.fullDay,
      blocks: r.fullDay ? undefined : mapBlocks(r.blocks || []),
      effectiveFrom: r.effectiveFrom || null,
      repeatTillDate: r.repeatTillDate ?? null
    }));

    return {
      tz: raw.tz,
      weeklyRoster,
      dayOfWeekRules,
      dayOfMonthRules,
      precedence: raw.precedence
    };
  }

  // ---------- Defaults ----------
  private defaultValue(): RosterConfig {
    return {
      tz: 'Asia/Kolkata',
      weeklyRoster: {
        effectiveFrom: null,
        repeatTillDate: null,
        days: {
          mon: [{ start: '09:00', end: '18:00' }],
          tue: [{ start: '09:00', end: '18:00' }],
          wed: [{ start: '09:00', end: '18:00' }],
          thu: [{ start: '09:00', end: '18:00' }],
          fri: [{ start: '09:00', end: '18:00' }],
          sat: [],
          sun: []
        }
      },
      dayOfWeekRules: [],
      dayOfMonthRules: [],
      precedence: { order: ['weeklyRoster', 'dayOfWeekRules', 'dayOfMonthRules'], conflict: 'include_wins' }
    };
  }


  getDowBlocks(i: number): FormArray {
    return this.dowRulesFA().at(i).get('blocks') as FormArray;
  }
  getDomBlocks(i: number): FormArray {
    return this.domRulesFA().at(i).get('blocks') as FormArray;
  }
}
